class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }
}

customElements.define('main-search', MainSearch);

class SearchModeTabs extends HTMLElement {
  connectedCallback() {
    this.root = this.closest('.template-search');
    this.buttons = Array.from(this.querySelectorAll('[data-search-mode]'));
    this.panels = Array.from(this.root?.querySelectorAll('[data-search-panel]') || []);
    this.onHashChange = this.handleHashChange.bind(this);

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.setMode(button.dataset.searchMode, true));
      button.addEventListener('keydown', (event) => this.handleKeydown(event));
    });

    this.setMode(this.modeFromHash(), false);
    window.addEventListener('hashchange', this.onHashChange);
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.onHashChange);
  }

  handleHashChange() {
    this.setMode(this.modeFromHash(), false);
  }

  handleKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const currentIndex = this.buttons.indexOf(event.currentTarget);
    let targetIndex = currentIndex;
    if (event.key === 'ArrowLeft') targetIndex = Math.max(0, currentIndex - 1);
    if (event.key === 'ArrowRight') targetIndex = Math.min(this.buttons.length - 1, currentIndex + 1);
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = this.buttons.length - 1;

    const target = this.buttons[targetIndex];
    target.focus();
    this.setMode(target.dataset.searchMode, true);
  }

  setMode(requestedMode, updateUrl) {
    const availableModes = this.buttons.map((button) => button.dataset.searchMode);
    const mode = availableModes.includes(requestedMode) ? requestedMode : 'keyword';
    delete document.documentElement.dataset.searchInitialMode;
    this.root.dataset.searchMode = mode;

    this.buttons.forEach((button) => {
      const selected = button.dataset.searchMode === mode;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.searchPanel !== mode;
    });

    if (updateUrl) {
      window.history.replaceState(null, '', this.urlForMode(mode));
      const activePanel = this.panels.find((panel) => panel.dataset.searchPanel === mode);
      activePanel?.querySelector('[data-search-panel-heading]')?.focus({ preventScroll: true });
      activePanel?.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  modeFromHash() {
    const hash = window.location.hash.toLowerCase();
    if (hash === '#imagesearch') return 'image';
    if (hash === '#sourcing') return 'sourcing';
    return 'keyword';
  }

  urlForMode(mode) {
    const base = `${window.location.pathname}${window.location.search}`;
    if (mode === 'image') return `${base}#ImageSearch`;
    if (mode === 'sourcing') return `${base}#Sourcing`;
    return base;
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('search-mode-tabs')) {
  customElements.define('search-mode-tabs', SearchModeTabs);
}
